import {
  ProviderAvailabilityRange,
  ProviderGeneralAvailability,
  ProviderRegistration,
  ProviderRegistrationConsiderationsEnum,
  ProviderRegistrationGenderDisclosureInnerTypeEnum,
  ProviderRegistrationGenderPreferenceEnum,
  ProviderRegistrationProfessionalCredentialsInnerTypeEnum,
  ProviderRegistrationSearchResult,
  ProviderService,
} from "@asd/customer-portal-api";

export const PROVIDER_REGISTRATION_TITLE_PROPS = {
  fontSize: "1.3rem",
  fontWeight: "bold",
};

// --- TYPE MAPS ---
const TYPE_MAPS = {
  GENDER: {
    [ProviderRegistrationGenderDisclosureInnerTypeEnum.female]: "Female",
    [ProviderRegistrationGenderDisclosureInnerTypeEnum.male]: "Male",
    [ProviderRegistrationGenderDisclosureInnerTypeEnum.non_binary]: "Non-binary",
    [ProviderRegistrationGenderDisclosureInnerTypeEnum.self_described]: "Other",
    [ProviderRegistrationGenderDisclosureInnerTypeEnum.no_answer]: "Prefer not to answer",
  },
  LANGUAGES: [
    { code: "en", name: "English" },
    { code: "af", name: "Afrikaans" },
    { code: "sq", name: "Albanian" },
    { code: "am", name: "Amharic" },
    { code: "ar", name: "Arabic" },
    { code: "hy", name: "Armenian" },
    { code: "az", name: "Azerbaijani" },
    { code: "eu", name: "Basque" },
    { code: "be", name: "Belarusian" },
    { code: "bn", name: "Bengali" },
    { code: "bs", name: "Bosnian" },
    { code: "bg", name: "Bulgarian" },
    { code: "my", name: "Burmese" },
    { code: "zh", name: "Chinese" },
    { code: "hr", name: "Croatian" },
    { code: "cs", name: "Czech" },
    { code: "da", name: "Danish" },
    { code: "nl", name: "Dutch" },
    { code: "et", name: "Estonian" },
    { code: "fil", name: "Filipino" },
    { code: "fi", name: "Finnish" },
    { code: "fr", name: "French" },
    { code: "gl", name: "Galician" },
    { code: "ka", name: "Georgian" },
    { code: "de", name: "German" },
    { code: "el", name: "Greek" },
    { code: "he", name: "Hebrew" },
    { code: "hi", name: "Hindi" },
    { code: "hu", name: "Hungarian" },
    { code: "is", name: "Icelandic" },
    { code: "id", name: "Indonesian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "jv", name: "Javanese" },
    { code: "km", name: "Khmer" },
    { code: "ko", name: "Korean" },
    { code: "ky", name: "Kyrgyz" },
    { code: "lo", name: "Lao" },
    { code: "lv", name: "Latvian" },
    { code: "lt", name: "Lithuanian" },
    { code: "mk", name: "Macedonian" },
    { code: "ms", name: "Malay" },
    { code: "mt", name: "Maltese" },
    { code: "mn", name: "Mongolian" },
    { code: "ne", name: "Nepali" },
    { code: "no", name: "Norwegian" },
    { code: "ps", name: "Pashto" },
    { code: "fa", name: "Persian" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "pa", name: "Punjabi" },
    { code: "ro", name: "Romanian" },
    { code: "ru", name: "Russian" },
    { code: "sr", name: "Serbian" },
    { code: "si", name: "Sinhala" },
    { code: "sk", name: "Slovak" },
    { code: "sl", name: "Slovenian" },
    { code: "so", name: "Somali" },
    { code: "es", name: "Spanish" },
    { code: "sw", name: "Swahili" },
    { code: "sv", name: "Swedish" },
    { code: "tg", name: "Tajik" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "th", name: "Thai" },
    { code: "tr", name: "Turkish" },
    { code: "tk", name: "Turkmen" },
    { code: "uk", name: "Ukrainian" },
    { code: "ur", name: "Urdu" },
    { code: "uz", name: "Uzbek" },
    { code: "vi", name: "Vietnamese" },
    { code: "yo", name: "Yoruba" },
    { code: "zu", name: "Zulu" },
  ],
  SERVICE: {
    [ProviderService.lawn_care]: "Lawn Care",
    [ProviderService.light_housekeeping]: "Light Housekeeping",
    [ProviderService.meal_preparation]: "Meal Preparation",
    [ProviderService.personal_care]: "Personal Care (e.g., bathing, dressing)",
    [ProviderService.shopping_errands]: "Shopping/Errands",
    [ProviderService.snow_removal]: "Snow Removal",
    [ProviderService.self_admin_medication_support]: "Supporting Self-Administration of Medication",
    [ProviderService.transportation]: "Transportation",
  },
  DISTANCE: {
    [0]: "Not willing to travel",
    [5]: "< 5 miles",
    [10]: "< 10 miles",
    [15]: "< 15 miles",
    [20]: "< 20 miles",
    [21]: "21+ miles",
  },
  GENDER_PREFERENCE: {
    [ProviderRegistrationGenderPreferenceEnum.none]: "No Preference",
    [ProviderRegistrationGenderPreferenceEnum.female]: "Female",
    [ProviderRegistrationGenderPreferenceEnum.male]: "Male",
  },
  CONSIDERATIONS: {
    [ProviderRegistrationConsiderationsEnum.non_smoking]: "Non-smoking environment required",
    [ProviderRegistrationConsiderationsEnum.no_pet]: "No pet environment",
    [ProviderRegistrationConsiderationsEnum.no_children]: "No children environment",
    [ProviderRegistrationConsiderationsEnum.intense_behavior_needs]:
      "Willing to work with individuals with intense behavioral health needs",
    [ProviderRegistrationConsiderationsEnum.physical_limitations]: "Physical limitations",
    [ProviderRegistrationConsiderationsEnum.none]: "None",
  },
  CREDENTIALS: {
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.cdcp]: "Certified Direct Care Professional (CDCP) Certification",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.cma]: "Certified Medical Assistant (CMA)",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.cna]: "Certified Nursing Assistant (CNA)",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.first_aid_cpr]: "First Aid/CPR Training",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.lpa_lva]: "Licensed Practical/Vocational Nurse (LPA/LVA)",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.mandt_cpi]: "MANDT System or Crisis Prevention Institute (CPI) Certification",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.rn]: "Registered Nurse (RN)",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.other]: "Other",
    [ProviderRegistrationProfessionalCredentialsInnerTypeEnum.not_applicable]: "N/A",
  },
};

// --- TYPES ---
type GenderEnumValues =
  (typeof ProviderRegistrationGenderDisclosureInnerTypeEnum)[keyof typeof ProviderRegistrationGenderDisclosureInnerTypeEnum];
type ServiceEnumValues = (typeof ProviderService)[keyof typeof ProviderService];
type GenderPreferenceEnumValues = (typeof ProviderRegistrationGenderPreferenceEnum)[keyof typeof ProviderRegistrationGenderPreferenceEnum];
type ConsiderationEnumValues = (typeof ProviderRegistrationConsiderationsEnum)[keyof typeof ProviderRegistrationConsiderationsEnum];
type CredentialEnumValues =
  (typeof ProviderRegistrationProfessionalCredentialsInnerTypeEnum)[keyof typeof ProviderRegistrationProfessionalCredentialsInnerTypeEnum];

// --- EXPORTS
export const GENDER_OPTIONS = Object.entries(TYPE_MAPS.GENDER) as [GenderEnumValues, string][];
export const LANGUAGES = TYPE_MAPS.LANGUAGES as { code: string; name: string }[];
export const SERVICE_OPTIONS = Object.entries(TYPE_MAPS.SERVICE) as [ServiceEnumValues, string][];
export const DISTANCE_OPTIONS = Object.entries(TYPE_MAPS.DISTANCE) as [string, string][];
export const GENDER_PREFERENCES = Object.entries(TYPE_MAPS.GENDER_PREFERENCE) as [GenderPreferenceEnumValues, string][];
export const CONSIDERATION_OPTIONS = Object.entries(TYPE_MAPS.CONSIDERATIONS) as [ConsiderationEnumValues, string][];
export const CREDENTIAL_OPTIONS = Object.entries(TYPE_MAPS.CREDENTIALS) as [CredentialEnumValues, string][];

const parseTime = (time: string): number => {
  const [hours, mins] = time.split(":").map(Number);
  return hours * 60 + mins;
};

const isThirtyMinsLater = (timeA: string, timeB: string) => parseTime(timeB) - parseTime(timeA) === 30;

const addThirtyMins = (time: string) => {
  const [hours, mins] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, mins + 30);
  return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
};

export const mapToUiGeneralAvailability = (availability: ProviderGeneralAvailability = {}): Date[] => {
  const days: (keyof ProviderGeneralAvailability)[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  // Create the reference date in the local timezone, not UTC
  const startOfWeek = new Date(2026, 1, 1, 0, 0, 0); // Feb 1st, 2026

  const entries = Object.entries(availability ?? {}) as [keyof ProviderGeneralAvailability, ProviderAvailabilityRange[]][];

  return entries.flatMap(([day, ranges]) => {
    const dayIndex = days.indexOf(day);
    if (dayIndex === -1 || !ranges) {
      return [];
    }

    return ranges.flatMap((range: ProviderAvailabilityRange) => {
      const timestamps: Date[] = [];
      let curr = parseTime(range.startTime);
      const end = parseTime(range.endTime);

      while (curr < end) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + dayIndex);
        date.setHours(Math.floor(curr / 60));
        date.setMinutes(curr % 60);

        timestamps.push(date);
        curr += 30;
      }
      return timestamps;
    });
  });
};

export const mapToProviderGeneralAvailability = (schedule: Date[]): undefined | ProviderGeneralAvailability => {
  const days: (keyof ProviderGeneralAvailability)[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const grouped: Record<string, string[]> = {};

  schedule.forEach((date) => {
    const day = days[date.getDay()];
    const time = `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
    if (!grouped[day]) {
      grouped[day] = [];
    }
    grouped[day].push(time);
  });

  const result: ProviderGeneralAvailability = {};
  Object.keys(grouped).forEach((day) => {
    const times = grouped[day].sort();
    if (!times.length) {
      return;
    }

    const ranges: ProviderAvailabilityRange[] = [];

    let start = times[0],
      current = times[0];

    for (let i = 1; i <= times.length; i++) {
      const next = times[i];
      if (next && isThirtyMinsLater(current, next)) {
        current = next;
      } else {
        ranges.push({ startTime: start, endTime: addThirtyMins(current) });
        start = next;
        current = next;
      }
    }
    result[day as keyof ProviderGeneralAvailability] = ranges;
  });

  return result;
};

export const flattenRegistration = (formData: any): ProviderRegistration => ({
  publicProvider: !!formData.publicProvider,
  ...formData.contact,
  ...formData.demographics,
  ...(formData.demographics?.languages?.length && {
    languages: formData.demographics.languages.map(({ code }: { code: string }) => code),
  }),
  ...(formData.services && {
    services: formData.services,
  }),
  ...formData.distance,
  ...(formData.availability?.maxHoursWeek != null && {
    maxHoursWeek: parseInt(formData.availability.maxHoursWeek, 10),
  }),
  ...(formData.availability?.generalAvailability?.length && {
    generalAvailability: mapToProviderGeneralAvailability(formData.availability.generalAvailability),
  }),
  ...formData.workPreferences,
  ...(formData.considerations && {
    considerations: formData.considerations,
  }),
  ...formData.experience,
});

export interface ProviderRegistrationForDisplay extends Omit<ProviderRegistrationSearchResult, "registration"> {
  registration: Omit<
    ProviderRegistration,
    | "genderDisclosure"
    | "languages"
    | "services"
    | "generalAvailability"
    | "genderPreference"
    | "considerations"
    | "professionalCredentials"
  > & {
    genderDisclosure?: { key: ProviderRegistrationGenderDisclosureInnerTypeEnum; value?: string }[];
    languages?: { code: string; name: string }[];
    services?: { key: ServiceEnumValues; value: string }[];
    generalAvailability?: Date[];
    genderPreference?: { key?: ProviderRegistrationGenderPreferenceEnum; value: string };
    considerations?: { key: ConsiderationEnumValues; value: string }[];
    professionalCredentials?: { key: CredentialEnumValues; value: string }[];
  };
}

export const getProviderForDisplay = (provider: ProviderRegistrationSearchResult): ProviderRegistrationForDisplay => {
  return {
    ...provider,
    registration: {
      ...provider.registration,
      genderDisclosure: provider.registration.genderDisclosure?.map((g) => ({
        key: g.type,
        value: g.type === ProviderRegistrationGenderDisclosureInnerTypeEnum.self_described ? g.selfDescription : TYPE_MAPS.GENDER[g.type],
      })),
      languages: (provider.registration.languages ?? [])
        .map((code) => TYPE_MAPS.LANGUAGES.find((lang) => lang.code === code))
        .filter((lang) => lang !== undefined),
      services: provider.registration.services?.map((s) => ({
        key: s,
        value: TYPE_MAPS.SERVICE[s as ServiceEnumValues] ?? s,
      })),
      generalAvailability: mapToUiGeneralAvailability(provider.registration.generalAvailability),
      genderPreference: {
        key: provider.registration.genderPreference,
        value:
          TYPE_MAPS.GENDER_PREFERENCE[provider.registration.genderPreference as ProviderRegistrationGenderPreferenceEnum] ??
          provider.registration.genderPreference,
      },
      considerations: provider.registration.considerations?.map((c) => ({
        key: c,
        value: TYPE_MAPS.CONSIDERATIONS[c as ConsiderationEnumValues] ?? c,
      })),
      professionalCredentials: provider.registration.professionalCredentials?.map((c) => ({
        key: c.type,
        value:
          c.type === ProviderRegistrationProfessionalCredentialsInnerTypeEnum.other
            ? (c.otherText ?? "")
            : (TYPE_MAPS.CREDENTIALS[c.type as CredentialEnumValues] ?? c.type),
      })),
    },
  };
};
